import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService, NotificationItem } from '../../../core/service/notification/notification.service';
import { NotificationRouteService } from '../../../core/service/notification/notification-route.service';

// Known event keys get a real French label. Anything not listed (a new event
// declared later via Signal's Catalog, e.g.) falls back to a generic humanizer
// below rather than showing nothing or crashing.
const EVENT_LABELS: Record<string, string> = {
  'licence:demande:submitted': 'Nouvelle demande soumise',
  'licence:demande:validated': 'Demande validée',
  'licence:demande:rejected': 'Demande rejetée',
  'licence:demande:cancelled_by_club': 'Demande annulée par le club',
  'licence:demande:cancelled_by_fede': 'Demande annulée par la fédération',
  'licence:licenceperiod:closing': 'Fin de période de licence',
  'licence:clubs:validated': 'Club validé',
  'licence:clubs:rejected': 'Club rejeté',
  'licence:clubs:status_changed': 'Statut du club modifié',
  'licence:joueurs:status_changed': 'Statut joueur modifié',
  'licence:joueurs:licence_deactivated': 'Licence joueur désactivée',
  'licence:joueurs:licence_reactivated': 'Licence joueur réactivée',
  'licence:dirigeants:status_changed': 'Statut dirigeant modifié',
  'licence:dirigeants:licence_deactivated': 'Licence dirigeant désactivée',
  'licence:dirigeants:licence_reactivated': 'Licence dirigeant réactivée',
  'licence:staff:status_changed': 'Statut staff modifié',
  'licence:staff:licence_deactivated': 'Licence staff désactivée',
  'licence:staff:licence_reactivated': 'Licence staff réactivée',
  'licence:arbitres:status_changed': 'Statut arbitre modifié',
  'licence:arbitres:licence_deactivated': 'Licence arbitre désactivée',
  'licence:arbitres:licence_reactivated': 'Licence arbitre réactivée',
  'licence:seasons:status_changed': 'Statut de saison modifié',
};

function humanizeEventKey(key: string): string {
  if (EVENT_LABELS[key]) return EVENT_LABELS[key];
  // Fallback: "project:module:action" -> "Module action" (best-effort, not curated)
  const parts = key.split(':');
  const words = parts.slice(1).join(' ').replace(/[_-]/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'à l\'instant';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

@Component({
  selector: 'app-notification-bell',
  standalone: false,
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationService);
  private notificationRoute = inject(NotificationRouteService);
  private router = inject(Router);

  notifications = this.notificationService.notifications;
  unreadCount = this.notificationService.unreadCount;
  isOpen = signal(false);

  humanizeEventKey = humanizeEventKey;
  timeAgo = timeAgo;

  ngOnInit(): void {
    this.notificationService.connect();
  }

  ngOnDestroy(): void {
    this.notificationService.disconnect();
  }

  toggle(): void {
    this.isOpen.update(v => !v);
  }

  close(): void {
    this.isOpen.set(false);
  }

  // Always navigates to the notification's own detail page (2026-08-06) — see
  // NotificationRouteService for how each event_key maps to a route. Marks read
  // regardless of whether a route resolves, since the user did open it either way.
  onNotificationClick(item: NotificationItem): void {
    this.notificationService.markRead(item.message_fed_seq);
    this.close();

    const route = this.notificationRoute.routeFor(item.event_key, item.payload);
    if (route) this.router.navigate(route);
  }

  markAllRead(): void {
    this.notificationService.markAllRead();
  }
}
